
import { useState } from 'react';
import { X, Settings2, Workflow, Code2, Boxes, ChevronRight, ArrowRightLeft } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AslDefinition } from '@/types/workflow-graph';

// =============================================================================
// Types
// =============================================================================

interface WorkflowMetadata {
    name: string;
    version?: string;
    description?: string;
    type?: string;
    adapter?: string;
    scope?: string;
    created?: string;
    modified?: string;
    capabilities?: Record<string, WorkflowCapability>;
}

interface WorkflowCapability {
    description?: string;
    input?: JsonSchema;
    output?: JsonSchema;
}

interface JsonSchema {
    type?: string;
    properties?: Record<string, JsonSchemaProperty>;
    required?: string[];
    items?: JsonSchemaProperty;
    description?: string;
}

interface JsonSchemaProperty {
    type?: string;
    description?: string;
    items?: JsonSchemaProperty;
    properties?: Record<string, JsonSchemaProperty>;
}

interface EditWorkflowDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    workflowName: string;
    workflowKey: string;
    definition: AslDefinition;
    metadata?: WorkflowMetadata;
    readOnly?: boolean;
    onSave?: (updates: Partial<WorkflowMetadata>) => void;
}

type TabId = 'general' | 'capabilities' | 'asl';

const tabs: Array<{ id: TabId; label: string; icon: typeof Settings2 }> = [
    { id: 'general', label: 'General', icon: Settings2 },
    { id: 'capabilities', label: 'Capabilities', icon: Boxes },
    { id: 'asl', label: 'Raw ASL', icon: Code2 },
];

// =============================================================================
// Sub-components
// =============================================================================

function TabButton({
    tab,
    isActive,
    onClick,
}: {
    tab: (typeof tabs)[0];
    isActive: boolean;
    onClick: () => void;
}) {
    const Icon = tab.icon;
    return (
        <button
            onClick={onClick}
            className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px',
                isActive
                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            )}
        >
            <Icon size={16} />
            {tab.label}
        </button>
    );
}

function GeneralTab({
    metadata,
    workflowName,
    readOnly,
}: {
    metadata?: WorkflowMetadata;
    workflowName: string;
    readOnly: boolean;
}) {
    return (
        <div className="space-y-5 p-6">
            {/* Name */}
            <div className="space-y-2">
                <Label>Workflow Name</Label>
                <Input
                    type="text"
                    value={metadata?.name || workflowName}
                    disabled={readOnly}
                    className="w-full"
                />
            </div>

            {/* Description */}
            <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                    value={metadata?.description || ''}
                    disabled={readOnly}
                    placeholder="Describe what this workflow does..."
                    rows={4}
                    className="resize-none"
                />
            </div>

            {/* Version */}
            <div className="space-y-2">
                <Label>Version</Label>
                <Input
                    type="text"
                    value={metadata?.version || '1.0.0'}
                    disabled={readOnly}
                    className="w-full font-mono"
                    placeholder="1.0.0"
                />
            </div>

            {/* Adapter */}
            {metadata?.adapter && (
                <div className="space-y-2">
                    <Label>Adapter</Label>
                    <Input
                        type="text"
                        value={metadata.adapter}
                        disabled={true}
                        className="w-full bg-slate-50 font-mono text-sm"
                    />
                    <p className="text-xs text-slate-400">
                        Runtime adapter for state persistence and execution
                    </p>
                </div>
            )}

            {/* Scope */}
            <div className="space-y-2">
                <Label>Scope</Label>
                <Input
                    type="text"
                    value={metadata?.scope || 'global'}
                    disabled={readOnly}
                    className="w-full"
                />
                <p className="text-xs text-slate-400">
                    Visibility scope for this workflow
                </p>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Created At</Label>
                    <Input
                        type="text"
                        value={metadata?.created ? new Date(metadata.created).toLocaleString() : '—'}
                        disabled={true}
                        className="w-full bg-slate-50 text-sm"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Updated At</Label>
                    <Input
                        type="text"
                        value={metadata?.modified ? new Date(metadata.modified).toLocaleString() : '—'}
                        disabled={true}
                        className="w-full bg-slate-50 text-sm"
                    />
                </div>
            </div>
        </div>
    );
}

function CapabilitiesTab({ metadata }: { metadata?: WorkflowMetadata }) {
    const capabilities = metadata?.capabilities || {};
    const capabilityEntries = Object.entries(capabilities);

    if (capabilityEntries.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center">
                <Boxes className="h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500">No capabilities defined</p>
                <p className="text-xs text-slate-400 mt-1">
                    Capabilities define the methods this workflow exposes
                </p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-slate-100">
            {capabilityEntries.map(([name, capability]) => (
                <CapabilityCard key={name} name={name} capability={capability} />
            ))}
        </div>
    );
}

function CapabilityCard({
    name,
    capability,
}: {
    name: string;
    capability: WorkflowCapability;
}) {
    const [isExpanded, setIsExpanded] = useState(true);

    const inputProperties = capability.input?.properties || {};
    const outputProperties = capability.output?.properties || {};
    const requiredInputs = capability.input?.required || [];

    return (
        <div className="p-4">
            {/* Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center gap-3 text-left group"
            >
                <ChevronRight
                    size={16}
                    className={cn(
                        'text-slate-400 transition-transform',
                        isExpanded && 'rotate-90'
                    )}
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-slate-900">
                            {name}()
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            method
                        </Badge>
                    </div>
                    {capability.description && (
                        <p className="text-xs text-slate-500 mt-0.5">
                            {capability.description}
                        </p>
                    )}
                </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="mt-4 ml-7 space-y-4">
                    {/* Input Parameters */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <ArrowRightLeft size={12} className="text-blue-500" />
                            <span className="text-xs font-medium text-slate-700">
                                Input Parameters
                            </span>
                        </div>
                        {Object.keys(inputProperties).length > 0 ? (
                            <div className="space-y-1.5">
                                {Object.entries(inputProperties).map(([propName, prop]) => (
                                    <ParameterRow
                                        key={propName}
                                        name={propName}
                                        property={prop}
                                        isRequired={requiredInputs.includes(propName)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 italic ml-4">
                                No input parameters
                            </p>
                        )}
                    </div>

                    {/* Output Parameters */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <ArrowRightLeft size={12} className="text-emerald-500 rotate-180" />
                            <span className="text-xs font-medium text-slate-700">
                                Output
                            </span>
                        </div>
                        {Object.keys(outputProperties).length > 0 ? (
                            <div className="space-y-1.5">
                                {Object.entries(outputProperties).map(([propName, prop]) => (
                                    <ParameterRow
                                        key={propName}
                                        name={propName}
                                        property={prop}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 italic ml-4">
                                No output defined
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function ParameterRow({
    name,
    property,
    isRequired = false,
}: {
    name: string;
    property: JsonSchemaProperty;
    isRequired?: boolean;
}) {
    return (
        <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-slate-800">{name}</code>
                    <Badge
                        variant="secondary"
                        className="text-[9px] px-1 py-0 font-mono bg-slate-200 text-slate-600"
                    >
                        {property.type || 'any'}
                    </Badge>
                    {isRequired && (
                        <Badge className="text-[9px] px-1 py-0 bg-red-100 text-red-700 border-0">
                            required
                        </Badge>
                    )}
                </div>
                {property.description && (
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                        {property.description}
                    </p>
                )}
            </div>
        </div>
    );
}

function RawAslTab({ definition }: { definition: AslDefinition }) {
    const aslJson = JSON.stringify(definition, null, 2);

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-3">
                <Label>Amazon States Language (ASL) Definition</Label>
                <button
                    onClick={() => navigator.clipboard.writeText(aslJson)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                    Copy to clipboard
                </button>
            </div>
            <pre className="rounded-lg bg-slate-900 p-4 text-xs text-slate-100 font-mono overflow-auto max-h-[calc(90vh-280px)]">
                <code>{aslJson}</code>
            </pre>
        </div>
    );
}

// =============================================================================
// Main Component
// =============================================================================

export default function EditWorkflowDrawer({
    isOpen,
    onClose,
    workflowName,
    workflowKey,
    definition,
    metadata,
    readOnly = true,
}: EditWorkflowDrawerProps) {
    const [activeTab, setActiveTab] = useState<TabId>('general');

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                        <Workflow className="h-5 w-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Workflow Settings
                                        </h2>
                                        <p className="text-sm text-slate-500 font-mono">
                                            {workflowKey}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-slate-200 bg-white px-8">
                            {tabs.map((tab) => (
                                <TabButton
                                    key={tab.id}
                                    tab={tab}
                                    isActive={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                />
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto bg-white py-4 px-12">
                            {activeTab === 'general' && (
                                <GeneralTab
                                    metadata={metadata}
                                    workflowName={workflowName}
                                    readOnly={readOnly}
                                />
                            )}
                            {activeTab === 'capabilities' && (
                                <CapabilitiesTab metadata={metadata} />
                            )}
                            {activeTab === 'asl' && <RawAslTab definition={definition} />}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg"
                            >
                                Close
                            </button>
                        </div>
        </SlideDrawer>
    );
}

